import { ArrowBigLeft, Trash2 } from 'lucide-react';
import React from 'react';

import { Typography, withTooltip } from '@crossnokaye/ux-common';

import { Button } from '@/ux-common/components/button/button';
import { Inline } from '@/ux-common/components/inline/inline';

import { SaveStatusIcon } from './save-status-icon';
import type { PreviewItemData, PreviewSaveStatus } from './setting-preview.types';

const SaveStatusIconTrigger = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ children, className, ...props }, ref) => (
  <span ref={ref} className={`inline-flex cursor-pointer ${className ?? ''}`.trim()} {...props}>
    {children}
  </span>
));

SaveStatusIconTrigger.displayName = 'SaveStatusIconTrigger';

const SaveStatusIconTriggerWithTooltip = withTooltip(SaveStatusIconTrigger);

interface PreviewItemProps {
  /** The change data to display (label, current/original values, id). */
  item: PreviewItemData;
  /**
   * When set, renders this message instead of the field-level value comparison.
   * Used for structural changes (e.g. sequencer config, defrost schedule).
   */
  message?: string;
  /** Called when the user clicks the discard (trash) button. */
  onDiscard: () => void;
  /** Save status for this item. */
  saveStatus?: PreviewSaveStatus;
  /** Prevents destructive actions while save is in progress. */
  disableDiscard?: boolean;
  /** Validation warning shown at review-time before save. */
  validationMessage?: string;
}

/**
 * PreviewItem
 *
 * Item component that displays a single pending setting change with visual comparison.
 * Shows the setting label, old-to-new value transition with arrow, and provides a button
 * to discard the individual change. Form-library-agnostic — the consumer provides a
 * simple `onDiscard` callback with no arguments.
 */
export const PreviewItem = ({
  item,
  message,
  onDiscard,
  saveStatus = 'idle',
  disableDiscard = false,
  validationMessage,
}: PreviewItemProps): React.ReactElement => {
  const isSubmitted = ['pending', 'queued', 'completed', 'failed', 'cancelled'].includes(
    saveStatus
  );

  if (message) {
    return (
      <div
        className="flex bg-ckds-neutral-800/50 p-2 items-center w-full gap-2"
        data-component="PreviewItem"
      >
        <SaveStatusIcon status={saveStatus} />
        <Typography variant="body" className="text-sm flex-1">
          {message}
        </Typography>
        <Button
          className="btn btn-tier2 btn-sm btn-square"
          onClick={onDiscard}
          disabled={disableDiscard || isSubmitted}
          aria-label={`Discard ${item.label} changes`}
        >
          <Trash2 className="btn-icon" />
        </Button>
      </div>
    );
  }

  const currentValue = item.displayCurrentValue ?? item.displayCurrent?.split(' ')[0];
  let currentUnits = item.displayCurrentUnits ?? item.displayCurrent?.split(' ')[1];

  if (currentUnits?.toUpperCase() === 'INHG') {
    currentUnits = 'PSIG';
  }

  return (
    <div
      className="grid bg-ckds-neutral-800/50 p-2 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center w-full gap-2"
      data-component="PreviewItem"
    >
      <Inline className="inline-row-sm flex-nowrap overflow-hidden">
        {saveStatus === 'validation-warning' && validationMessage ? (
          <SaveStatusIconTriggerWithTooltip
            aria-label={validationMessage}
            tooltipContent={validationMessage}
            delayDuration={0}
          >
            <SaveStatusIcon status={saveStatus} />
          </SaveStatusIconTriggerWithTooltip>
        ) : (
          <SaveStatusIcon status={saveStatus} />
        )}
        <Typography variant="body" className="text-sm font-semibold uppercase truncate">
          {item.label}
        </Typography>
      </Inline>
      <div className="overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1">
          <p className="text-sm text-ckds-input-fg-new text-right truncate">
            {currentValue} {currentUnits?.toUpperCase()}
          </p>
          <ArrowBigLeft className="glyph glyph-sm text-ckds-input-fg-new" aria-hidden="true" />
          <p className="truncate">{item.displayOriginal}</p>
        </div>
      </div>
      <Button
        className="btn btn-tier2 btn-sm btn-square"
        onClick={onDiscard}
        disabled={disableDiscard || isSubmitted}
        aria-label={`Discard change for ${item.label}`}
      >
        <Trash2 className="btn-icon" />
      </Button>
    </div>
  );
};
